import React from 'react';
import { Callout, FileWithUrl, FlexContainer, UtilityInfoIcon } from '@placer-ui/components';
import { ControlledInputField } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-input-field';
import { ControlledTextArea } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-textarea';
import { SupportRequestModalForm } from 'features/offline-reports/support-requests/components/support-requests-modals/components/support-request-modal-form';
import { ControlledFileUpload } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-file-upload';
import { SupportEmailInput } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-support-email-input/support-email-input';
import type {
    FormControl,
    SupportRequestsData,
} from 'features/offline-reports/support-requests/components/support-requests-modals/types';
import {
    SupportOpportunityUrlInput,
    SupportSalesEmailInput,
} from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-support-sales-email-inputs/support-sales-email-inputs';
import { getFeatureFlags } from 'core/flow-control';
import styles from './request-region.module.scss';

const MAX_NAME_LENGTH = 100;
const MAX_ADDITIONAL_INFO_LENGTH = 1000;
const SUPPORTED_FILE_TYPES = [
    '.kmz', // KMZ (compressed KML)
    '.kml', // KML (Google Earth format)
    'application/pdf', // PDF (Portable Document Format)
    'application/zip', // SHP ZIP (Shapefile archive)
    'application/x-zip',
    'application/x-zip-compressed',
    'application/geo+json',
    '.geojson',
];

export interface RequestRegionFormValues {
    type: 'region';
    poiName: string;
    additionalInformation: string;
    files: FileWithUrl[];
    executiveEmail?: string;
    opportunityUrl?: string;
}

type RequestRegionProps = SupportRequestsData['region'] & {
    form?: FormControl<RequestRegionFormValues>;
};

export const convertRegionFormToRequestPayload = (values: RequestRegionFormValues) => ({
    ...values,
    files: values.files.map(({ file }) => file),
});

export const RequestRegion = ({ form, ...props }: RequestRegionProps) => {
    const { enable_request_properties_sales_flow_ff } = getFeatureFlags();

    return (
        <SupportRequestModalForm<RequestRegionFormValues>
            testId="request-region"
            requestType="region"
            title="Request a Region"
            subTitle="Request a large polygon like a CBSA, Zip Code, City or County"
            form={form}
            defaultValues={{ ...(props ?? {}), type: 'region', files: [] }}
            convertToRequestPayload={convertRegionFormToRequestPayload}
        >
            <div className={styles.formContainer}>
                {enable_request_properties_sales_flow_ff && (
                    <FlexContainer gap={16}>
                        <SupportSalesEmailInput />
                        <SupportOpportunityUrlInput />
                    </FlexContainer>
                )}
                {!enable_request_properties_sales_flow_ff && <SupportEmailInput />}
                <ControlledInputField
                    name="poiName"
                    label="Region Name"
                    placeholder="Region Name"
                    data-testid="region-name-input"
                    maxLength={MAX_NAME_LENGTH}
                    asterix
                    required
                />
                <ControlledTextArea
                    name="additionalInformation"
                    initialHeight={100}
                    maxLength={MAX_ADDITIONAL_INFO_LENGTH}
                    label="Additional Information"
                    data-testid="region-additional-information-input"
                    feedback={[
                        {
                            message: `Up to ${MAX_ADDITIONAL_INFO_LENGTH} characters`,
                            state: 'guidance',
                            hideIcon: true,
                        },
                    ]}
                    placeholder="Share additional details to help our support team assist you better (optional)."
                />
                <ControlledFileUpload
                    name="files"
                    testId="region-files-input"
                    label="Attachments"
                    formats={SUPPORTED_FILE_TYPES}
                    description="ZIP (SHP file), KMZ, KML, GeoJSON"
                    feedback={[]}
                />
                <Callout
                    data-testid="request-region-callout"
                    FreeTextIcon={<UtilityInfoIcon />}
                    text="Please upload a Zipped Shapefile or KMZ for the region you are requesting. This is not required, but is useful."
                />
            </div>
        </SupportRequestModalForm>
    );
};
